import Link from 'next/link';
import React, {useState} from 'react';
import {useDebounce} from 'react-use';

import PasswordWithRulesCard from '@src/components/organisms/PasswordWithRulesCard';
import Button from '@src/components/atoms/Button';
import FormErrorMessage from '@src/components/atoms/FormErrorMessage';
import TextField from '@src/components/atoms/TextField';
import {
  AuthForm,
  AuthFormTitle,
  AuthFormWrapper,
  AuthFromSection,
  FooterOptionText,
} from '@src/components/forms/AuthForm';
import useAuth from '@src/hooks/useAuth';
import {
  validateConfirmPassword,
  validateEmail,
  ValidateField,
  validatePassword,
  validateRequiredFields,
  validationHasError,
} from '@src/utils/validations';

const TWO_SECONDS = 2000;
const NO_ERROR_MESSAGE = '';
const DEFAULT_REQUEST_ERROR_MESSAGE = 'Something went wrong. Try again.';

interface SignUpFormFields {
  firstName: string;
  lastName: string;
  schoolName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const signUpFormValidate = (formData: SignUpFormFields): ValidateField => ({
  firstName: validateRequiredFields(formData.firstName),
  lastName: validateRequiredFields(formData.lastName),
  schoolName: validateRequiredFields(formData.schoolName),
  email: validateEmail(formData.email),
  password: validatePassword(formData.password),
  confirmPassword: validateConfirmPassword(formData.password, formData.confirmPassword),
});

const initialState: SignUpFormFields = {
  email: '',
  firstName: '',
  lastName: '',
  schoolName: '',
  password: '',
  confirmPassword: '',
};

const SignUpForm = () => {
  const {logIn, signUp} = useAuth();

  const [formData, setFormData] = useState<SignUpFormFields>(initialState);
  const [formErrorMessage, setFormErrorMessage] = useState<string>(NO_ERROR_MESSAGE);
  const [validatedFields, setValidatedFields] = useState<ValidateField>({});
  const isFormValid = !validationHasError(validatedFields);

  const handleValidateFields = () => {
    const validation = signUpFormValidate(formData);
    setValidatedFields(validation);
  };

  const handleInputChange = (value: string, field: string) => {
    setFormData(previousValues => ({
      ...previousValues,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormErrorMessage(NO_ERROR_MESSAGE);
    if (!isFormValid) return;

    try {
      const {confirmPassword, ...signUpFormData} = formData;
      await signUp(signUpFormData);
      await logIn({email: formData.email, password: formData.password});
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      const errorStatusCode = error?.response?.data?.statusCode;

      if (errorStatusCode === 400 && errorMessage) {
        setFormErrorMessage(errorMessage);
      } else {
        setFormErrorMessage(DEFAULT_REQUEST_ERROR_MESSAGE);
      }
    }
  };

  useDebounce(handleValidateFields, TWO_SECONDS, [formData]);

  return (
    <AuthFormWrapper>
      <AuthFormTitle>Sign Up</AuthFormTitle>

      {formErrorMessage && <FormErrorMessage message={formErrorMessage} />}

      <AuthForm onSubmit={handleSubmit}>
        <AuthFromSection>
          <TextField
            label="Email"
            name="email"
            type="email"
            inputMode="email"
            error={validatedFields?.email?.error}
            isValid={validatedFields?.email?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
          <TextField
            label="First Name"
            name="firstName"
            error={validatedFields?.firstName?.error}
            isValid={validatedFields?.firstName?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            error={validatedFields?.lastName?.error}
            isValid={validatedFields?.lastName?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
          <TextField
            label="School Name"
            name="schoolName"
            error={validatedFields?.schoolName?.error}
            isValid={validatedFields?.schoolName?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
          <PasswordWithRulesCard
            error={validatedFields?.password?.error}
            isValid={validatedFields?.password?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            password={formData.password}
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            error={validatedFields?.confirmPassword?.error}
            isValid={validatedFields?.confirmPassword?.isValid}
            onChange={handleInputChange}
            onBlur={handleValidateFields}
            required
          />
        </AuthFromSection>
        <AuthFromSection>
          <Button type="submit" disabled={!isFormValid} fullWidth>
            Sign Up
          </Button>

          <FooterOptionText>
            Already have an account?{' '}
            <Link href="/signin" passHref>
              <a>Sign in instead.</a>
            </Link>
          </FooterOptionText>
        </AuthFromSection>
      </AuthForm>
    </AuthFormWrapper>
  );
};

export default SignUpForm;
